import { CardSkeleton } from "@/components/atoms/CardSkeleton/CardSkeleton";
import { ConnectionOwnerNotice } from "@/components/molecules/ConnectionOwnerNotice";
import { GoogleAnalyticsAddButton } from "@/components/molecules/GoogleAnalyticsAddButton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { IncludeExcludeToggle } from "@/components/ui/include-exclude-toggle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDashboardUrlParams } from "@/hooks/useDashboardUrlParams";
import { useDomainIntegrations } from "@/hooks/useDomainIntegrations";
import { useGoogleAnalyticsConnection } from "@/hooks/useGoogleAnalyticsConnection";
import { useGoogleAnalyticsData } from "@/hooks/useGoogleAnalyticsData";
import { useOrgRole } from "@/hooks/useOrgRole";
import { useSonnerToast } from "@/hooks/useSonnerToast";
import { useStore } from "@/store";
import { Loader2 } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

interface GoogleAnalyticsSectionProps {
  responsiveButtons?: boolean;
  showTitle?: boolean;
  showHeader?: boolean;
  showEmptyState?: boolean;
}

export interface GoogleAnalyticsSectionRef {
  enterEdit: () => void;
}

const getTimestampMillis = (
  timestamp: Date | { toDate?: () => Date } | null | undefined,
) => {
  if (!timestamp) return 0;
  if (timestamp instanceof Date) return timestamp.getTime();
  if (typeof timestamp.toDate === "function") return timestamp.toDate().getTime();
  return 0;
};

export const GoogleAnalyticsSection = forwardRef<
  GoogleAnalyticsSectionRef,
  GoogleAnalyticsSectionProps
>(function GoogleAnalyticsSection(
  {
    responsiveButtons = false,
    showTitle = true,
    showHeader = true,
    showEmptyState = true,
  },
  ref,
) {
  // Get current user and domain from store
  const {
    currentUser,
    currentDomain,
    orgId,
    generateDomainPagesInsights,
    getIsConnectionOwner,
    getMemberDisplayName,
  } = useStore();
  const { params, clearParams } = useDashboardUrlParams();
  const { toast } = useSonnerToast();
  const { isAdmin } = useOrgRole();

  // Connection management hook
  const { connectNewAccount, disconnectAndPurge } =
    useGoogleAnalyticsConnection();

  // Create stable userId to prevent hook re-initialization
  const userId = useMemo(() => currentUser?.uid || null, [currentUser?.uid]);

  // Data subscription hooks
  const { accounts, hasConnectedAccounts, getPropertiesForAccount } =
    useGoogleAnalyticsData(userId);

  const {
    googleAnalyticsIntegration,
    updateGoogleAnalytics,
    setGoogleAnalyticsEnabled,
  } = useDomainIntegrations(orgId, currentDomain?.id || null);

  // Get current domain's selection (for account/property)
  const selectedAccountId = googleAnalyticsIntegration.selectedAccountId;
  const selectedPropertyId = googleAnalyticsIntegration.selectedPropertyId;

  const hasAccounts = hasConnectedAccounts();
  const isDomainConfigured = !!(selectedAccountId && selectedPropertyId);
  const [isConnecting, setIsConnecting] = useState(false);

  // Track pending gaEdit from OAuth redirect (to handle before accounts load)
  const [pendingGaEdit, setPendingGaEdit] = useState(false);

  // Disconnect dialog and loading state
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Local edit state and draft selections
  const [isEditing, setIsEditing] = useState(false);
  const [draftAccountId, setDraftAccountId] = useState<string | undefined>(
    selectedAccountId ?? undefined,
  );
  const [draftPropertyId, setDraftPropertyId] = useState<string | undefined>(
    selectedPropertyId ?? undefined,
  );

  const isConnectionOwner = getIsConnectionOwner(googleAnalyticsIntegration);
  const connectedByName = getMemberDisplayName(
    googleAnalyticsIntegration.connectedBy ?? "",
  );

  // Get display values for view mode. `accounts` only ever contains the
  // *viewer's* connected accounts, so it is empty for anyone who did not
  // connect this integration -- fall back to it only when the domain doc
  // has no denormalized display strings (e.g. pre-migration connections).
  const selectedAccount = useMemo(
    () => accounts.find((acc) => acc.account_id === selectedAccountId),
    [accounts, selectedAccountId],
  );

  const selectedProperty = useMemo(() => {
    if (!selectedAccountId) return null;
    const properties = getPropertiesForAccount(selectedAccountId);
    return properties.find((prop) => prop.property_id === selectedPropertyId);
  }, [selectedAccountId, selectedPropertyId, getPropertiesForAccount]);

  const accountDisplayName =
    googleAnalyticsIntegration.connectedAccountEmail ??
    selectedAccount?.account_email ??
    selectedAccount?.account_name ??
    null;
  const propertyDisplayName =
    googleAnalyticsIntegration.connectedPropertyName ??
    selectedProperty?.display_name ??
    null;

  // Find the most recently connected account (by updated_at or created_at)
  const mostRecentAccount = useMemo(() => {
    if (accounts.length === 0) return null;
    if (accounts.length === 1) return accounts[0];

    return accounts.reduce((mostRecent, account) => {
      const mostRecentTime = mostRecent.updated_at || mostRecent.created_at;
      const accountTime = account.updated_at || account.created_at;

      if (!accountTime) return mostRecent;
      if (!mostRecentTime) return account;

      return getTimestampMillis(accountTime) > getTimestampMillis(mostRecentTime)
        ? account
        : mostRecent;
    });
  }, [accounts]);

  // Keep drafts in sync on external selection changes (e.g., elsewhere in app)
  useEffect(() => {
    if (!isEditing) {
      setDraftAccountId(selectedAccountId ?? undefined);
      setDraftPropertyId(selectedPropertyId ?? undefined);
    }
  }, [isEditing, selectedAccountId, selectedPropertyId]);

  // Capture gaEdit param from URL immediately (before accounts load)
  useEffect(() => {
    if (params.gaEdit) {
      setPendingGaEdit(true);
      // Clean up the URL immediately to avoid persisting the param
      clearParams(["gaEdit"]);
    }
  }, [params.gaEdit, clearParams]);

  // Apply pending gaEdit when accounts become available
  // Auto-select the most recently connected account so user only needs to pick property
  useEffect(() => {
    if (pendingGaEdit && hasAccounts) {
      // If no account is selected yet, auto-select the most recently connected one
      const accountToSelect =
        selectedAccountId ?? mostRecentAccount?.account_id;
      setDraftAccountId(accountToSelect ?? undefined);
      setDraftPropertyId(selectedPropertyId ?? undefined);
      setIsEditing(true);
      setPendingGaEdit(false);
    }
  }, [
    pendingGaEdit,
    hasAccounts,
    selectedAccountId,
    selectedPropertyId,
    mostRecentAccount,
  ]);

  const enterEdit = () => {
    setDraftAccountId(selectedAccountId ?? undefined);
    setDraftPropertyId(selectedPropertyId ?? undefined);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    const originalAccountId = selectedAccountId ?? undefined;
    const originalPropertyId = selectedPropertyId ?? undefined;

    setDraftAccountId(originalAccountId);
    setDraftPropertyId(originalPropertyId);
    setIsEditing(false);
  };

  const saveConnection = async () => {
    if (!draftAccountId || !draftPropertyId) return;

    // At save time the editor is the owner, so `accounts` is populated for
    // them -- resolve the display strings now and denormalize them onto the
    // domain doc so any org member can render them later.
    const account = accounts.find((acc) => acc.account_id === draftAccountId);
    const property = getPropertiesForAccount(draftAccountId).find(
      (prop) => prop.property_id === draftPropertyId,
    );

    await updateGoogleAnalytics(draftAccountId, draftPropertyId, {
      accountEmail: account?.account_email ?? account?.account_name ?? null,
      propertyName: property?.display_name ?? null,
    });
    setIsEditing(false);

    if (!orgId || !currentDomain?.id) return;
    try {
      const { triggered } = await generateDomainPagesInsights(
        orgId,
        currentDomain.id,
      );

      if (triggered > 0) {
        toast({
          variant: "success",
          title: "Generating insights",
          description: `Started insight generation for ${triggered} page${triggered === 1 ? "" : "s"}.`,
        });
      }
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Failed to start insight generation",
        description:
          err instanceof Error
            ? err.message
            : "Unable to start Google Analytics insight generation.",
      });
    }
  };

  const handleDisconnect = async () => {
    if (!currentUser?.uid || !currentDomain?.id || !selectedAccountId) {
      return;
    }

    setIsDisconnecting(true);
    try {
      await disconnectAndPurge(selectedAccountId, currentDomain.id);

      // Close the dialog
      setShowDisconnectDialog(false);

      // Show success toast
      toast({
        variant: "success",
        title: "Google Analytics disconnected",
        description:
          "Your Google Analytics connection and data have been removed from this and other domains.",
      });

      // No follow-up write here: `disconnectAndPurge` already deleted the
      // integration doc server-side. A merge `setDoc(null, null)` against a
      // now-nonexistent doc would be treated as a create by firestore.rules,
      // which denies it (no connectedBy in the payload) -- the onSnapshot
      // listener in domainIntegrationsSlice already maps the deleted doc to
      // null fields, so local state clears on its own.
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";

      // Check if it's a "running jobs" error
      if (errorMessage.includes("running")) {
        toast({
          variant: "destructive",
          title: "Cannot disconnect",
          description:
            "There are Google Analytics jobs currently running. Please wait for them to complete before disconnecting.",
          closeButton: true,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Failed to disconnect",
          description: errorMessage,
          closeButton: true,
        });
      }
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectDialog(false);
    }
  };

  // Expose enterEdit via ref
  useImperativeHandle(ref, () => ({
    enterEdit,
  }));

  const buttonClassName = responsiveButtons
    ? "text-sm md:text-base h-9 md:h-10 px-4 md:px-6"
    : "";

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="flex justify-between items-center">
          {showTitle && (
            <div className="flex items-center space-x-1">
              <h3 className="text-lg font-medium">Google Analytics</h3>
            </div>
          )}
          <div className={`flex gap-2 ${!showTitle ? "ml-auto" : ""}`}>
            {/* A domain someone else already connected has nothing this
                button could legitimately do for a non-owner: "select
                existing"/"connect new" would only kick off the viewer's own
                OAuth flow and any resulting save is rejected by
                firestore.rules (update requires connectedBy == caller uid).
                Hide it rather than offer a dead-end affordance -- the
                ConnectionOwnerNotice below already explains who owns it.
                Anyone below admin is in the same position on an unconnected
                domain: connecting writes the integration doc, which
                firestore.rules only lets an admin create. Editors work
                *through* this connection, never on it. */}
            {isAdmin && (!isDomainConfigured || isConnectionOwner) && (
              <GoogleAnalyticsAddButton
                hasConnectedAccounts={hasAccounts}
                isConnecting={isConnecting}
                onSelectExisting={enterEdit}
                onConnectNew={async () => {
                  setIsConnecting(true);
                  try {
                    const url = new URL(window.location.href);
                    url.searchParams.set("sidebarOpen", "true");
                    url.searchParams.set("gaEdit", "true");
                    await connectNewAccount(url.toString());
                  } catch (err) {
                    console.error("Connection error:", err);
                  } finally {
                    setIsConnecting(false);
                  }
                }}
                className={buttonClassName}
              />
            )}
          </div>
        </div>
      )}

      {!isDomainConfigured && !isEditing && isConnecting ? (
        <CardSkeleton />
      ) : !isDomainConfigured && !isEditing && showEmptyState ? (
        <p className="text-sm text-muted-foreground">
          No Google Analytics connection configured yet
        </p>
      ) : !isDomainConfigured && !isEditing ? null : isEditing ? (
        // Edit mode with dropdowns
        <Card className="overflow-hidden py-3">
          <div className="px-3 md:px-4 space-y-4">
            <div className="space-y-2">
              <span className="text-base font-medium">Google account</span>
              <Select
                value={draftAccountId ?? ""}
                onValueChange={(val) => {
                  setDraftAccountId(val);
                  setDraftPropertyId(undefined);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((acc) => (
                    <SelectItem key={acc.account_id} value={acc.account_id}>
                      {acc.account_email || acc.account_name || acc.account_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <span className="text-base font-medium">Property ID</span>
              <Select
                value={draftPropertyId ?? ""}
                onValueChange={(val) => {
                  setDraftPropertyId(val);
                }}
                disabled={!draftAccountId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {(draftAccountId
                    ? getPropertiesForAccount(draftAccountId)
                    : []
                  ).map((prop) => (
                    <SelectItem key={prop.property_id} value={prop.property_id}>
                      {prop.display_name || prop.property_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={cancelEdit}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveConnection}
                disabled={!draftAccountId || !draftPropertyId}
              >
                Save
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        // View mode with ItemCard-like layout
        <Card className="overflow-hidden py-3 relative">
          {isDisconnecting && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm rounded-lg z-10">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-6 w-6 animate-spin text-destructive" />
              </div>
            </div>
          )}
          <div className="flex items-stretch gap-3 px-3 md:px-4">
            <div className="flex-1 min-w-0 space-y-3">
              {/* Google account */}
              <div className="space-y-1">
                <h4 className="text-base font-medium">Google account</h4>
                <p className="text-sm text-muted-foreground">
                  {accountDisplayName || "—"}
                </p>
              </div>

              {/* Property ID */}
              <div className="space-y-1">
                <h4 className="text-base font-medium">Property ID</h4>
                <p className="text-sm text-muted-foreground">
                  {propertyDisplayName || "—"}
                </p>
              </div>
            </div>

            <div className="w-px bg-gray-200 self-stretch" />

            <div className="flex flex-col gap-2 flex-shrink-0 w-24">
              <IncludeExcludeToggle
                id="ga-enabled"
                checked={googleAnalyticsIntegration.is_enabled}
                onCheckedChange={(checked) =>
                  setGoogleAnalyticsEnabled(Boolean(checked))
                }
                disabled={!isAdmin || !isConnectionOwner}
              />
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={enterEdit}
                disabled={!isAdmin || !isConnectionOwner}
              >
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setShowDisconnectDialog(true)}
                disabled={!isAdmin || isDisconnecting || !isConnectionOwner}
                title="Disconnect Google Analytics from this domain"
              >
                Disconnect
              </Button>
              <ConnectionOwnerNotice
                isOwner={isConnectionOwner}
                connectedBy={googleAnalyticsIntegration.connectedBy}
                connectedByName={connectedByName}
              />
            </div>
          </div>
        </Card>
      )}

      {/* Disconnect confirmation dialog */}
      <AlertDialog
        open={showDisconnectDialog}
        onOpenChange={setShowDisconnectDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Google Analytics?</AlertDialogTitle>
            <AlertDialogDescription>
              This will disconnect your Google Analytics account from this and
              other domains and remove all generated insights data.
              <br />
              <br />
              <strong>Note:</strong> This action cannot be undone. You can
              reconnect Google Analytics at any time, but previously generated
              insights will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDisconnecting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {isDisconnecting ? "Disconnecting..." : "Disconnect"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
});
